import { styled } from '@mui/material/styles';
import { Box, FormLabel, FormHelperText } from '@mui/material';
import { FormFieldValidationState } from './FormField.types';
import { designTokens } from '../../tokens';

/**
 * Validation state styles
 */
const getValidationStyles = (validationState: FormFieldValidationState) => {
  const tokens = designTokens.colors;

  switch (validationState) {
    case 'error':
      return {
        '& .MuiFormLabel-root': {
          color: tokens.error[700],
        },
        '& .MuiFormHelperText-root': {
          color: tokens.error[700],
        },
      };

    case 'success':
      return {
        '& .MuiFormLabel-root': {
          color: tokens.success[700],
        },
        '& .MuiFormHelperText-root': {
          color: tokens.success[700],
        },
      };

    case 'warning':
      return {
        '& .MuiFormLabel-root': {
          color: tokens.warning[700],
        },
        '& .MuiFormHelperText-root': {
          color: tokens.warning[700],
        },
      };

    case 'default':
    default:
      return {
        '& .MuiFormLabel-root': {
          color: tokens.neutral[700],
        },
        '& .MuiFormHelperText-root': {
          color: tokens.neutral[600],
        },
      };
  }
};

/**
 * Styled FormField container
 */
export const StyledFormFieldContainer = styled(Box, {
  shouldForwardProp: (prop) => !['validationState'].includes(prop as string),
})<{
  validationState: FormFieldValidationState;
}>(({ validationState }) => ({
  ...getValidationStyles(validationState),

  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: designTokens.spacing[1],
}));

/**
 * Styled FormLabel
 */
export const StyledFormLabel = styled(FormLabel)(() => ({
  fontSize: designTokens.typography.fontSize.sm,
  fontWeight: designTokens.typography.fontWeight.medium,
  fontFamily: designTokens.typography.fontFamily.primary,
  marginBottom: designTokens.spacing[1],
  display: 'flex',
  alignItems: 'center',
  gap: designTokens.spacing[1],

  '&.Mui-required::after': {
    content: '"*"',
    color: designTokens.colors.error[500],
    marginLeft: designTokens.spacing[1],
  },
}));

/**
 * Styled FormHelperText
 */
export const StyledFormHelperText = styled(FormHelperText)(() => ({
  fontSize: designTokens.typography.fontSize.xs,
  fontFamily: designTokens.typography.fontFamily.primary,
  marginTop: designTokens.spacing[1],
  marginLeft: 0,
}));

/**
 * Styled Optional indicator
 */
export const StyledOptionalText = styled('span')(() => ({
  fontSize: designTokens.typography.fontSize.xs,
  fontWeight: designTokens.typography.fontWeight.regular,
  color: designTokens.colors.neutral[500],
  marginLeft: designTokens.spacing[1],
}));

/**
 * Styled Character count
 */
export const StyledCharacterCount = styled('span')<{ warning?: boolean }>(
  ({ warning }) => ({
    fontSize: designTokens.typography.fontSize.xs,
    fontWeight: designTokens.typography.fontWeight.medium,
    color: warning
      ? designTokens.colors.warning[500]
      : designTokens.colors.neutral[600],
    marginLeft: 'auto',
  })
);
